/**
 * Centralized data-testid constants for E2E testing
 *
 * Naming convention: COMPONENT_ELEMENT_ACTION
 * Example: SIDEBAR_NEW_CHAT_BUTTON
 *
 * Usage in components:
 *   import { TEST_IDS } from '@/constants/testIds';
 *   <button data-testid={TEST_IDS.SIDEBAR_NEW_CHAT_BUTTON}>
 *
 * Usage in tests:
 *   import { TEST_IDS } from '../../constants/testIds';
 *   page.locator(`[data-testid="${TEST_IDS.SIDEBAR_NEW_CHAT_BUTTON}"]`)
 */

export const TEST_IDS = {
  // ============================================
  // Authentication
  // ============================================
  AUTH_EMAIL_INPUT: 'auth-email-input',
  AUTH_PASSWORD_INPUT: 'auth-password-input',
  AUTH_SUBMIT_BUTTON: 'auth-submit-button',
  AUTH_ERROR_MESSAGE: 'auth-error-message',
  AUTH_LOADING_SPINNER: 'auth-loading-spinner',

  // ============================================
  // Sidebar
  // ============================================
  SIDEBAR: 'sidebar',
  SIDEBAR_TOGGLE: 'sidebar-toggle',
  SIDEBAR_NEW_CHAT_BUTTON: 'sidebar-new-chat-button',
  SIDEBAR_SEARCH_BUTTON: 'sidebar-search-button',
  SIDEBAR_LIBRARY_BUTTON: 'sidebar-library-button',
  SIDEBAR_DOCUMENTS_BUTTON: 'sidebar-documents-button',

  // User Menu
  SIDEBAR_USER_MENU: 'sidebar-user-menu',
  SIDEBAR_USER_NAME: 'sidebar-user-name',
  SIDEBAR_USER_EMAIL: 'sidebar-user-email',
  SIDEBAR_SETTINGS_BUTTON: 'sidebar-settings-button',
  SIDEBAR_NOTIFICATIONS_BUTTON: 'sidebar-notifications-button',
  SIDEBAR_LOGOUT_BUTTON: 'sidebar-logout-button',
  SIDEBAR_THEME_TOGGLE: 'sidebar-theme-toggle',

  // Thread/History
  THREAD_LIST: 'thread-list',
  THREAD_ITEM: 'thread-item',
  THREAD_ITEM_TITLE: 'thread-item-title',
  THREAD_ITEM_MENU: 'thread-item-menu',

  // Projects
  PROJECT_LIST: 'project-list',
  PROJECT_ITEM: 'project-item',
  PROJECT_ITEM_MENU: 'project-item-menu',

  // ============================================
  // Chat Interface
  // ============================================
  CHAT_CONTAINER: 'chat-container',
  CHAT_GREETING: 'chat-greeting',

  // Chat Input
  CHAT_INPUT: 'chat-input',
  CHAT_INPUT_CONTAINER: 'chat-input-container',
  CHAT_SEND_BUTTON: 'chat-send-button',
  CHAT_STOP_BUTTON: 'chat-stop-button',
  CHAT_VOICE_BUTTON: 'chat-voice-button',
  CHAT_WEB_SEARCH_TOGGLE: 'chat-web-search-toggle',

  // Messages
  CHAT_MESSAGE_LIST: 'chat-message-list',
  CHAT_MESSAGE_USER: 'chat-message-user',
  CHAT_MESSAGE_ASSISTANT: 'chat-message-assistant',
  CHAT_MESSAGE_CONTENT: 'chat-message-content',
  CHAT_MESSAGE_ACTIONS: 'chat-message-actions',
  CHAT_MESSAGE_COPY_BUTTON: 'chat-message-copy-button',
  CHAT_MESSAGE_EDIT_BUTTON: 'chat-message-edit-button',
  CHAT_MESSAGE_DELETE_BUTTON: 'chat-message-delete-button',
  CHAT_MESSAGE_LOADING: 'chat-message-loading',
  CHAT_MESSAGE_ERROR: 'chat-message-error',
  CHAT_MESSAGE_RETRY_BUTTON: 'chat-message-retry-button',

  // ============================================
  // File Upload & Attachments
  // ============================================
  FILE_UPLOAD_BUTTON: 'file-upload-button',
  FILE_UPLOAD_DROPDOWN: 'file-upload-dropdown',
  FILE_UPLOAD_LOCAL: 'file-upload-local',
  FILE_UPLOAD_GOOGLE_DRIVE: 'file-upload-google-drive',
  FILE_UPLOAD_TEXT_CONTENT: 'file-upload-text-content',
  FILE_UPLOAD_DRAW_SKETCH: 'file-upload-draw-sketch',

  FILE_PREVIEW_LIST: 'file-preview-list',
  FILE_PREVIEW_ITEM: 'file-preview-item',
  FILE_PREVIEW_REMOVE: 'file-preview-remove',

  FILE_ATTACHMENT: 'file-attachment',
  FILE_ATTACHMENT_IMAGE: 'file-attachment-image',
  FILE_ATTACHMENT_DOWNLOAD: 'file-attachment-download',

  // ============================================
  // Canvas
  // ============================================
  CANVAS_INTERFACE: 'canvas-interface',
  CANVAS_TOOLBAR: 'canvas-toolbar',
  CANVAS_CLOSE_BUTTON: 'canvas-close-button',
  CANVAS_EXPORT_BUTTON: 'canvas-export-button',
  CANVAS_SHARE_BUTTON: 'canvas-share-button',
  CANVAS_PREVIEW_TOGGLE: 'canvas-preview-toggle',
  CANVAS_UNDO_BUTTON: 'canvas-undo-button',
  CANVAS_REDO_BUTTON: 'canvas-redo-button',

  // Canvas Editors
  CANVAS_CODE_EDITOR: 'canvas-code-editor',
  CANVAS_TEXT_EDITOR: 'canvas-text-editor',
  CANVAS_SPREADSHEET_EDITOR: 'canvas-spreadsheet-editor',
  CANVAS_HTML_EDITOR: 'canvas-html-editor',
  CANVAS_PREVIEW: 'canvas-preview',

  // ============================================
  // Assistants
  // ============================================
  ASSISTANT_SELECTOR: 'assistant-selector',
  ASSISTANT_LIST: 'assistant-list',
  ASSISTANT_ITEM: 'assistant-item',
  ASSISTANT_CREATE_BUTTON: 'assistant-create-button',
  ASSISTANT_EDIT_BUTTON: 'assistant-edit-button',
  ASSISTANT_DELETE_BUTTON: 'assistant-delete-button',
  ASSISTANT_MENU_BUTTON: 'assistant-menu-button',

  // Assistant Form
  ASSISTANT_NAME_INPUT: 'assistant-name-input',
  ASSISTANT_DESCRIPTION_INPUT: 'assistant-description-input',
  ASSISTANT_MODEL_SELECT: 'assistant-model-select',
  ASSISTANT_INSTRUCTIONS_INPUT: 'assistant-instructions-input',
  ASSISTANT_SAVE_BUTTON: 'assistant-save-button',
  ASSISTANT_CANCEL_BUTTON: 'assistant-cancel-button',

  // ============================================
  // Workspace
  // ============================================
  WORKSPACE_SELECTOR: 'workspace-selector',
  WORKSPACE_LIST: 'workspace-list',
  WORKSPACE_ITEM: 'workspace-item',
  WORKSPACE_CREATE_BUTTON: 'workspace-create-button',
  WORKSPACE_NAME: 'workspace-name',

  // ============================================
  // Dialogs & Modals
  // ============================================
  DIALOG_OVERLAY: 'dialog-overlay',
  DIALOG_CONTENT: 'dialog-content',
  DIALOG_CLOSE_BUTTON: 'dialog-close-button',
  DIALOG_CONFIRM_BUTTON: 'dialog-confirm-button',
  DIALOG_CANCEL_BUTTON: 'dialog-cancel-button',

  // Search Dialog
  SEARCH_DIALOG: 'search-dialog',
  SEARCH_INPUT: 'search-input',
  SEARCH_RESULTS: 'search-results',
  SEARCH_RESULT_ITEM: 'search-result-item',

  // Settings Dialog
  SETTINGS_DIALOG: 'settings-dialog',
  SETTINGS_TAB: 'settings-tab',
  SETTINGS_CONTENT: 'settings-content',

  // ============================================
  // Errors & Loading States
  // ============================================
  ERROR_MESSAGE: 'error-message',
  ERROR_RETRY_BUTTON: 'error-retry-button',
  LOADING_SPINNER: 'loading-spinner',
  LOADING_SKELETON: 'loading-skeleton',

  // ============================================
  // Dynamic Content (for masking in visual tests)
  // ============================================
  TIMESTAMP: 'timestamp',
  USER_AVATAR: 'user-avatar',
  DYNAMIC_CONTENT: 'dynamic-content',
};

/**
 * Helper function to generate dynamic test IDs
 * @param {string} base - Base test ID
 * @param {...string} suffixes - Additional suffixes to append
 * @returns {string} Combined test ID
 *
 * @example
 * getTestId(TEST_IDS.THREAD_ITEM, threadId)
 * // Returns: "thread-item-abc123"
 *
 * @example
 * getTestId(TEST_IDS.FILE_ATTACHMENT, fileId, 'image')
 * // Returns: "file-attachment-xyz789-image"
 */
export const getTestId = (base, ...suffixes) =>
  [base, ...suffixes].filter(Boolean).join('-');

/**
 * Helper to create a data-testid selector string
 * @param {string} testId - The test ID value
 * @returns {string} CSS selector for the test ID
 *
 * @example
 * testIdSelector(TEST_IDS.CHAT_INPUT)
 * // Returns: '[data-testid="chat-input"]'
 */
export const testIdSelector = (testId) => `[data-testid="${testId}"]`;

export default TEST_IDS;
